import React, {useEffect, useMemo, useState} from "react";
import "./style.less";
import {useTranslation} from "react-i18next";
import {stripeServices} from "../../../../services/stripeServices";
import Loading from "../../../../components/Loading";
import moment from "moment";
import InfoCircleIcon from "../../../../components/svg/InfoCircle";
import Tooltip from "../../../../components/Tooltip";
import avatarFemale from "../../../../components/VisitCard/images/avatar_female.png";
import avatarMale from "../../../../components/VisitCard/images/avatar_male.png";

const PaymentPayout = ({payout, open, setOpen}) => {

    const {t} = useTranslation();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    const roundToFixed = (num, places) => {
        let mult = Math.pow(10,2);
        return (Math.round(num * mult) / mult).toFixed(places);
    }

    const handleToggleOpen = () => {
        setOpen(open === payout.id ? null : payout.id);
    }

    useEffect(() => {
        if(open === payout.id && transactions.length === 0) {
            (async () => {
                setLoading(true);
                try {
                    let _transactions = await stripeServices.getPayoutTransactions(payout.id);
                    setTransactions(_transactions);
                } catch (err) {
                    console.error("Error fetching payout transactions:", err);
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [open]);

    return (
        <div key={payout.id} onClick={handleToggleOpen}
             className={"Payments__payouts-payout" + ((open === payout.id) ? " open" : "") + (loading ? " loading" : "")}>
            <div className={"Payments__payouts-payout-summary"}>
                <span className={"payout-amount"}>€{roundToFixed(payout.amount / 100, 2)}</span>
                <span className={"payout-status"}>{payout.status.toUpperCase()}</span>
                <div className={"payout-account"}>
                    <p>{payout.destination.bank_name}</p>
                    <strong>{payout.destination.country} •••• {payout.destination.last4}</strong>
                </div>
                <span className={"payout-id"}>{payout.id}</span>
                <span className={"payout-date"}>{moment(payout.arrival_date * 1000).format("DD MMM YYYY")}</span>
            </div>
            <div className={"Payments__payouts-payout-transactions" + (loading ? " loading" : "")}>
                {loading ? <Loading relative/> : transactions.length > 0 ? <>
                    <div className={"transactions-header"}>
                        <strong className={"header-payment"}></strong>
                        <strong className={"header-tenant"}>{t('main_page.payments.tenant')}</strong>
                        <strong className={"header-reservation"}>{t('main_page.payments.reservation')}</strong>
                        <strong className={"header-date"}>{t('main_page.payments.moveIn')}</strong>
                        <strong className={"header-date"}>{t('main_page.payments.moveOut')}</strong>
                        <strong className={"header-date"}>{t('main_page.payments.confirmDate')}</strong>
                    </div>
                    {transactions.map(transaction => {
                        let propertyImage = transaction.reservation?.room_id ? transaction.reservation?.room?.photos?.["featured"]?.sm : transaction.reservation?.property?.photos?.["featured_0"][0]?.sm;
                        let reservationConfirmationDate = transaction.reservation.type === "booking" ? transaction.reservation.paymentDate : transaction.reservation.acceptedDate;
                        let reservationDetailsUrl = `/reservations/${transaction.reservation.id}/details`;
                        return <a href={reservationDetailsUrl} target={"_blank"} key={transaction.id} className={"transaction"} onClick={e => {
                            e.stopPropagation();
                        }}>
                            <span className={"transaction-amount"}>€{roundToFixed(transaction.net, 2)}</span>
                            <span className={"transaction-amount-info"}>
                                <Tooltip position='right'>
                                    <Tooltip.Icon>
                                        <InfoCircleIcon width={16} height={16} />
                                    </Tooltip.Icon>
                                    <Tooltip.Content>
                                        <p>Total: <strong>€{roundToFixed(transaction.amount, 2)}</strong></p>
                                        <p>Fees: <strong>€{roundToFixed(transaction.fee, 2)}</strong></p>
                                    </Tooltip.Content>
                                </Tooltip>
                            </span>
                            <div className={"transaction-tenant"}>
                                <div className="avatar-container"
                                     style={{ backgroundImage: `url(${transaction.reservation.user.photo || (transaction.reservation.user.personalDetails?.gender === "female" ? avatarFemale : avatarMale)})` }}>
                                    {transaction.reservation.user.personalDetails?.nationality && <div className={'RoundedCircleFlag'}>
                                        <div className={'CountryFlagCircle'}
                                             style={{backgroundImage: `url(https://github.com/madebybowtie/FlagKit/raw/master/Assets/PNG/${transaction.reservation.user.personalDetails.nationality.toUpperCase()}@2x.png?raw=true)`}}/>
                                    </div>}
                                </div>
                                <div className={"transaction-tenant-info"}>
                                    <small>{transaction.reservation.user.name}</small>
                                    <small>{transaction.reservation.user.email}</small>
                                </div>
                            </div>
                            <div className={"transaction-reservation"}>
                                <div className={"property-info"}>
                                    <div className={"property-info-image"} style={{backgroundImage: `url(${propertyImage})`}}/>
                                    <div className={"property-info-details"}>
                                        <strong>ID: {transaction.reservation.id.split("-")[0]}</strong>
                                        <small>
                                            {transaction.reservation.property.internal_name} ({transaction.reservation.room_id && (t('room') + " " + transaction.reservation.room.key.split("_")[1])})
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <span className={"transaction-date"}>{moment(transaction.reservation.moveIn).format("DD MMM YYYY")}</span>
                            <span className={"transaction-date"}>{moment(transaction.reservation.moveOut).format("DD MMM YYYY")}</span>
                            <span className={"transaction-date"}>{moment(reservationConfirmationDate).format("DD MMM YYYY")}</span>
                        </a>
                    })}
                </> : <p className={"no-transactions"}>{t('main_page.payments.no_transactions')}</p>}
            </div>
        </div>
    )
}

const Payments = ({}) => {
    const {t} = useTranslation();

    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noMore, setNoMore] = useState(false);
    const [open, setOpen] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                let _payouts = await stripeServices.getPayouts();
                setPayouts(_payouts);
            } catch (err) {
                console.error("Error fetching payouts:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const loadMore = async () => {
        if(payouts.length > 0) {
            setLoading(true);
            try {
                let lastPayout = payouts[payouts.length - 1];
                let _payouts = await stripeServices.getPayouts({starting_after: lastPayout.id});
                if (_payouts.length === 0) {
                    setNoMore(true);
                } else {
                    setPayouts(prev => [...prev, ..._payouts]);
                }
            } catch (err) {
                console.error("Error fetching more payouts:", err);
            } finally {
                setLoading(false);
            }
        }
    }

    return (
        <div className={"Payments container"}>
            <div className={"Payments__content card"}>
                <h2 className={"Payments__content-title"}>{t('main_page.payments.title')}</h2>
                {(loading && payouts.length === 0) ? <Loading/> : <div className={"Payments__payouts"}>
                    <div className={"Payments__payouts-header"}>
                        <strong className={"header-amount"}>{t('main_page.payments.amount')}</strong>
                        <strong className={"header-status"}></strong>
                        <strong className={"header-account"}>{t('main_page.payments.account')}</strong>
                        <strong className={"header-id"}>ID</strong>
                        <strong className={"header-date"}>{t('main_page.payments.date')}</strong>
                    </div>
                    <div className={"Payments__payouts-list"}>
                        {payouts.map(payout => {
                            return <PaymentPayout key={payout.id} payout={payout}
                                                  open={open} setOpen={setOpen}/>;
                        })}
                        {!noMore && <div className={"Payments__payouts-load-more"}>
                            <button className={"btn btn-primary"} disabled={loading} onClick={loadMore}>
                                {loading ? <Loading relative/> : t('main_page.payments.load_more')}
                            </button>
                        </div>}
                    </div>
                </div>}
            </div>
        </div>
    )
};

export default Payments;
